import { useEffect, useMemo, useState } from 'react';
import { darkTheme, lightTheme } from '../styles/theme';

export type ThemeModePreference = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'theme_mode';

function getSystemMode(): 'light' | 'dark' {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function useThemeMode() {
  const [preference, setPreference] = useState<ThemeModePreference>(() => {
    const saved = localStorage.getItem(STORAGE_KEY) as ThemeModePreference | null;
    return saved ?? 'system';
  });

  const [systemMode, setSystemMode] = useState<'light' | 'dark'>(() => getSystemMode());

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, preference);
  }, [preference]);

  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => setSystemMode(mql.matches ? 'dark' : 'light');
    handler();
    mql.addEventListener?.('change', handler);
    return () => mql.removeEventListener?.('change', handler);
  }, []);

  const resolvedMode: 'light' | 'dark' = preference === 'system' ? systemMode : preference;
  const theme = useMemo(() => (resolvedMode === 'dark' ? darkTheme : lightTheme), [resolvedMode]);

  const cyclePreference = () => {
    setPreference((p) => (p === 'system' ? 'light' : p === 'light' ? 'dark' : 'system'));
  };

  return { theme, preference, resolvedMode, setPreference, cyclePreference };
}

